import type { InfoSheetContent } from '@uiKit/components/InfoSheet.vue'
import {
  buildEstimatedRoiSheet,
  buildGrossYieldSheet,
  buildMarketPriceTile,
  buildSlicealPriceTile,
  type OfferingInfoTile,
} from '../../browse/constants/offeringInfo'
import type { OfferingFigures } from '../../browse/types/propertyTypes'
import type { PropertyDetail } from '../types/propertyDetailTypes'

// Pure builders for the detail page's figures: the Investment Breakdown card,
// the right rail's Property Financials and the projected-returns tiles the
// invest drawer reuses. The calculator lives in ./propertyCalculatorBuilders.ts.
// All money is AED; rates are percentages (6.5 = 6.5%).

const PERCENT_BASE = 100

const integerFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const rateFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 })

/** 92000 → "92,000". */
export const formatWhole = (amount: number): string => integerFormatter.format(Math.round(amount))

/** 8 → "8%", 6.5 → "6.5%", 9.0909 → "9.09%". */
export const formatRate = (pct: number): string => `${rateFormatter.format(pct)}%`

export const formatAedWhole = (amount: number): string => `AED ${formatWhole(amount)}`

const isPositive = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value) && value > 0

/** Net yield: the financials block first, then the offering's expected rental yield. */
export const netYieldOf = (data: PropertyDetail): number | null =>
  data.financials.netYieldPct ?? data.expectedRentalYield

/** The Sliceal price — the API `purchasePrice` (see propertyDetailParser). */
const slicealPriceOf = (data: PropertyDetail): number | null =>
  isPositive(data.purchasePrice) ? data.purchasePrice : null

/** Inputs for the shared ⓘ explainers the Properties cards already use. */
export const buildOfferingFigures = (data: PropertyDetail): OfferingFigures => {
  const listing = data.listingPrice
  const purchase = data.purchasePrice
  const slicealPrice = slicealPriceOf(data)
  const market = data.financials.currentMarketValue
  return {
    listingPrice: isPositive(listing) ? formatAedWhole(listing) : undefined,
    purchasePrice: isPositive(purchase) ? formatAedWhole(purchase) : undefined,
    marketValue: isPositive(market) ? formatAedWhole(market) : undefined,
    currentMarketValue: market ?? undefined,
    fundingTotal: listing,
    discountPct:
      isPositive(slicealPrice) && isPositive(market)
        ? ((market - slicealPrice) / market) * PERCENT_BASE
        : undefined,
    grossYieldPct: data.financials.grossYieldPct ?? undefined,
    annualRoiPct: data.annualRoiPct ?? undefined,
    appreciationPct: data.expectedCapitalAppreciation ?? undefined,
    annualRentalIncome: data.financials.annualRentalIncome ?? undefined,
  }
}

// ── Investment Breakdown card ────────────────────────────────────────────

export interface BreakdownRow {
  key: string
  label: string
  /** Formatted figure — a whole-dirham amount or a rate. */
  value: string
  /** Amount rows render the dirham glyph before the value. */
  isAmount: boolean
  /** Operating expenses: shown as "− ⃀ 17,250". */
  isDeduction?: boolean
  /** Rate under an amount, e.g. "9.09%" beneath the appreciation figure. */
  subValue?: string
  sheet?: InfoSheetContent
}

export interface BreakdownGroup {
  key: 'income' | 'yield' | 'returns'
  rows: BreakdownRow[]
}

// ⓘ copy below mirrors the mobile app's property-detail sheets word-for-word.

const ANNUAL_RENTAL_INCOME_SHEET: InfoSheetContent = {
  title: 'Annual Rental Income',
  note: 'The total rent this property is expected to earn in a year, before operating expenses',
  description:
    'Contracted annual rent from the Ejari. After operating expenses are deducted, you receive your net rental income in proportion to your ownership.',
}

const incomeRows = (data: PropertyDetail): BreakdownRow[] => {
  const gross = data.financials.annualRentalIncome
  const net = data.financials.netRentalIncome
  const expenses = gross != null && net != null ? Math.max(gross - net, 0) : null
  const rows: (BreakdownRow | null)[] = [
    gross != null
      ? {
          key: 'grossRent',
          label: 'Annual Rental Income',
          value: formatWhole(gross),
          isAmount: true,
          sheet: ANNUAL_RENTAL_INCOME_SHEET,
        }
      : null,
    expenses != null && gross != null && net != null
      ? {
          key: 'expenses',
          label: 'Operating Expenses',
          value: formatWhole(expenses),
          isAmount: true,
          isDeduction: true,
          sheet: {
            title: 'Operating Expenses',
            note: 'These are the yearly operating expenses which includes maintenance and other service charges',
            description:
              "The recurring annual costs of managing and maintaining a property. These are deducted from the property's annual rental income to determine the net rental income distributed to investors.",
            rows: [
              { label: 'Annual rental income', value: formatAedWhole(gross) },
              { operator: '−', label: 'Net rental income', value: formatAedWhole(net) },
              {
                operator: '=',
                label: 'Total Operating Expenses',
                value: formatAedWhole(expenses),
                isResult: true,
              },
            ],
          },
        }
      : null,
    net != null
      ? {
          key: 'netRent',
          label: 'Net Rental Income',
          value: formatWhole(net),
          isAmount: true,
          sheet: {
            title: 'Net Rental Income',
            note: 'The rent this property is expected to earn in a year, after operating expenses',
            description:
              'The yearly rent left after operating expenses have been deducted. This is the income figure your returns are based on.',
            rows:
              gross != null && expenses != null
                ? [
                    { label: 'Annual rental income', value: formatAedWhole(gross) },
                    {
                      operator: '−',
                      label: 'Operating expenses',
                      value: formatAedWhole(expenses),
                    },
                    {
                      operator: '=',
                      label: 'Net rental income',
                      value: formatAedWhole(net),
                      isResult: true,
                    },
                  ]
                : undefined,
          },
        }
      : null,
  ]
  return rows.filter((row): row is BreakdownRow => row !== null)
}

// Shown in words when the offering has no rent / price to substitute.
const NET_YIELD_FORMULA = 'Net yield (%) = (Net rental income/ Property Price) x 100'

const netYieldSheet = (data: PropertyDetail, netYield: number): InfoSheetContent => {
  const net = data.financials.netRentalIncome
  const slicealPrice = slicealPriceOf(data)
  const hasFigures = net != null && isPositive(slicealPrice)
  return {
    title: 'Net Yield',
    note: 'This is your estimated annual rental yield after expenses',
    description:
      "Estimated average rental yield of the unit over 1 year holding period. It shows the annual rental income as a percentage of the property's price - after all costs are deducted.",
    gutter: hasFigures ? 'always' : 'auto',
    rows: hasFigures
      ? [
          { label: 'Net rental income', value: formatAedWhole(net) },
          { operator: '÷', label: 'Sliceal price', value: formatAedWhole(slicealPrice) },
          { operator: '×', label: '', value: '100' },
          { operator: '=', label: 'Net yield', value: formatRate(netYield), isResult: true },
        ]
      : [{ label: NET_YIELD_FORMULA, value: '' }],
  }
}

const yieldRows = (data: PropertyDetail): BreakdownRow[] => {
  const gross = data.financials.grossYieldPct
  const net = netYieldOf(data)
  const rows: (BreakdownRow | null)[] = [
    gross != null
      ? {
          key: 'grossYield',
          label: 'Gross Yield',
          value: formatRate(gross),
          isAmount: false,
          sheet: buildGrossYieldSheet(buildOfferingFigures(data)),
        }
      : null,
    net != null
      ? {
          key: 'netYield',
          label: 'Net Yield',
          value: formatRate(net),
          isAmount: false,
          sheet: netYieldSheet(data, net),
        }
      : null,
  ]
  return rows.filter((row): row is BreakdownRow => row !== null)
}

const APPRECIATION_LABEL = 'Estimated Appreciation'

// Prose only — the app's sheet carries no formula.
const APPRECIATION_SHEET: InfoSheetContent = {
  title: APPRECIATION_LABEL,
  note: "The property's estimated yearly value growth based on historical DLD data.",
  description:
    'This amount is unrealised. You receive it when you sell your property on the secondary market.',
}

const FIVE_YEAR_ROI_LABEL = 'Estimated ROI (5Y)'
const FIVE_YEARS = 5

/**
 * (Net yield × 5) + ((1 + appreciation)^5 − 1), ending on the offering's own
 * 5-year target. Prose only when a rate the formula needs is missing.
 */
const fiveYearRoiSheet = (data: PropertyDetail, fiveYearRoi: number): InfoSheetContent => {
  const net = netYieldOf(data)
  const growth = data.expectedCapitalAppreciation
  const canWork = net != null && Number.isFinite(net) && growth != null && Number.isFinite(growth)
  return {
    title: FIVE_YEAR_ROI_LABEL,
    note: 'This is your estimated return on investment (ROI) over a 5 year period',
    description:
      "Your estimated total return on this investment over 5 years combining both the property's value growth and the rental income you're expected to receive.",
    rows: canWork
      ? [
          { label: 'Net yield × 5', value: `${(net * FIVE_YEARS).toFixed(2)}%` },
          {
            operator: '+',
            label: '(1 + appreciation)^5 − 1',
            value: `${(((1 + growth / PERCENT_BASE) ** FIVE_YEARS - 1) * PERCENT_BASE).toFixed(2)}%`,
          },
          {
            operator: '=',
            label: FIVE_YEAR_ROI_LABEL,
            value: `${fiveYearRoi.toFixed(2)}%`,
            isResult: true,
          },
        ]
      : undefined,
  }
}

const returnRows = (data: PropertyDetail): BreakdownRow[] => {
  // Rates only — no AED amount alongside.
  const rateRow = (
    key: string,
    label: string,
    pct: number,
    sheet?: InfoSheetContent,
  ): BreakdownRow => ({
    key,
    label,
    value: formatRate(pct),
    isAmount: false,
    sheet,
  })
  const appreciation = data.expectedCapitalAppreciation
  const annualRoi = data.annualRoiPct
  const fiveYearRoi = data.targetRoi5yr
  const rows: (BreakdownRow | null)[] = [
    appreciation != null
      ? rateRow('appreciation', APPRECIATION_LABEL, appreciation, APPRECIATION_SHEET)
      : null,
    annualRoi != null
      ? rateRow(
          'annualRoi',
          'Estimated ROI',
          annualRoi,
          buildEstimatedRoiSheet(buildOfferingFigures(data)),
        )
      : null,
    fiveYearRoi != null
      ? rateRow(
          'fiveYearRoi',
          FIVE_YEAR_ROI_LABEL,
          fiveYearRoi,
          fiveYearRoiSheet(data, fiveYearRoi),
        )
      : null,
  ]
  return rows.filter((row): row is BreakdownRow => row !== null)
}

/** Income / yield / return groups — empty groups are dropped. */
export const buildInvestmentBreakdown = (data: PropertyDetail | null): BreakdownGroup[] => {
  if (!data) return []
  const groups: BreakdownGroup[] = [
    { key: 'income', rows: incomeRows(data) },
    { key: 'yield', rows: yieldRows(data) },
    { key: 'returns', rows: returnRows(data) },
  ]
  return groups.filter((group) => group.rows.length > 0)
}

// ── Right rail: Property Financials ──────────────────────────────────────

/** The platform's own rate: a token is a fixed slice of area everywhere. */
const TOKENS_PER_SQM = 10_000
const MAX_TOKEN_PRICE_DECIMALS = 8
const MAX_AREA_DECIMALS = 2

const countFormatter = new Intl.NumberFormat('en-US')
const areaFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: MAX_AREA_DECIMALS })
const tokenPriceFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: MAX_TOKEN_PRICE_DECIMALS,
})

/**
 * Price Per Token: the offering's own supply sum (only when the area accounts
 * for the published supply) and the division the tapped price comes from.
 */
export const buildTokenPriceSheet = (data: PropertyDetail): InfoSheetContent => {
  const { totalTokens, tokenPrice } = data
  const slicealPrice = slicealPriceOf(data)
  const perToken = isPositive(tokenPrice)
    ? tokenPrice
    : isPositive(slicealPrice) && isPositive(totalTokens)
      ? slicealPrice / totalTokens
      : null
  const pricePerToken = perToken != null ? `AED ${tokenPriceFormatter.format(perToken)}` : undefined
  const sheet: InfoSheetContent = {
    title: 'Price Per Token',
    value: pricePerToken,
    note: 'What one token of this property costs.',
  }
  if (!pricePerToken || !isPositive(totalTokens) || !isPositive(slicealPrice)) return sheet

  const area = data.builtUpAreaSqm
  const tokens = countFormatter.format(totalTokens)
  const supplyRows =
    isPositive(area) && Math.round(area * TOKENS_PER_SQM) === totalTokens
      ? [
          { label: 'Area', value: `${areaFormatter.format(area)} sqm.` },
          { operator: '×', label: 'Tokens per sqm', value: countFormatter.format(TOKENS_PER_SQM) },
          { operator: '=', label: 'Total Tokens', value: tokens, isResult: true },
        ]
      : []
  // Two equations, as in the app: the supply sum (when it holds), then the
  // division the price comes from.
  const priceRows = [
    { label: 'Sliceal Price', value: formatAedWhole(slicealPrice) },
    { operator: '÷', label: 'Total Tokens', value: tokens },
    { operator: '=', label: 'Price per token', value: pricePerToken, isResult: true },
  ]
  return supplyRows.length
    ? { ...sheet, rows: supplyRows, extraRows: [priceRows] }
    : { ...sheet, rows: priceRows }
}

export interface TokenBreakdown {
  sold: number
  available: number
  total: number
  /** Share of all tokens already sold, 0–100. */
  soldPct: number
}

export interface PropertyFinancialsView {
  priceTile: OfferingInfoTile
  marketTile: OfferingInfoTile
  tokenPrice: number
  tokenPriceSheet: InfoSheetContent
  tokens: TokenBreakdown
}

export const buildPropertyFinancials = (
  data: PropertyDetail | null,
): PropertyFinancialsView | null => {
  if (!data) return null
  const figures = buildOfferingFigures(data)
  const total = data.totalTokens
  const sold = Math.max(data.tokensSold, 0)
  return {
    priceTile: buildSlicealPriceTile(figures),
    marketTile: buildMarketPriceTile(figures),
    tokenPrice: data.tokenPrice,
    tokenPriceSheet: buildTokenPriceSheet(data),
    tokens: {
      sold,
      available: Math.max(data.tokensAvailable, 0),
      total,
      soldPct: total > 0 ? Math.min((sold / total) * PERCENT_BASE, PERCENT_BASE) : 0,
    },
  }
}

// ── Projected returns (invest drawer tiles) ──────────────────────────────

export interface ReturnTile {
  key: 'netYield' | 'appreciation' | 'annualRoi'
  label: string
  value: string
  sheet?: InfoSheetContent
}

export const buildReturnTiles = (data: PropertyDetail | null): ReturnTile[] => {
  if (!data) return []
  const net = netYieldOf(data)
  const appreciation = data.expectedCapitalAppreciation
  const annualRoi = data.annualRoiPct
  const tiles: (ReturnTile | null)[] = [
    net != null
      ? {
          key: 'netYield',
          label: 'Net Yield',
          value: formatRate(net),
          sheet: netYieldSheet(data, net),
        }
      : null,
    appreciation != null
      ? {
          key: 'appreciation',
          label: APPRECIATION_LABEL,
          value: formatRate(appreciation),
          sheet: APPRECIATION_SHEET,
        }
      : null,
    annualRoi != null
      ? {
          key: 'annualRoi',
          label: 'Estimated ROI',
          value: formatRate(annualRoi),
          sheet: buildEstimatedRoiSheet(buildOfferingFigures(data)),
        }
      : null,
  ]
  return tiles.filter((tile): tile is ReturnTile => tile !== null)
}
